/**
 * Funding and deducting a customer's balance by hand.
 *
 * This is the only place in the console where an operator moves real money into or out of a
 * customer's wallet directly, so the parsing discipline is the strictest in the app and it is
 * the same discipline `lib/rate.ts` and `lib/money.ts` already keep: **no `Number`, no
 * `parseFloat`, no `Math.round(value * 100)`**. Everything below is `BigInt` arithmetic over
 * digits the operator typed. `Math.round(20_100_000.07 * 100)` is off by a kobo, and a kobo
 * that goes missing here goes missing out of somebody's actual balance.
 *
 * The narration is required rather than optional, and that is not a form-validation
 * preference. An adjustment without one is an unexplained balance change on a licensed
 * platform's ledger; the person who has to answer for it later is not the person who made
 * it, and "I remember what that was" is not an audit trail.
 *
 * ## The copy rule this module does *not* implement
 *
 * The customer-facing activity row for a credit must read **"Top Up"**, carry the platform
 * icon, and reveal nothing about an operator having made it. That is the API's job — the row
 * is written server-side inside the same transaction as the posting — and the contract now
 * states it explicitly on `adminAdjustBalanceRequestSchema`: the narration is an operator note
 * that goes into the ledger metadata and the audit row, and the serializer, not the person
 * typing, is what keeps it away from the customer. The console has no way to write a
 * customer's activity feed and should not acquire one.
 */
import type { MoneyDTO } from '../contracts/types.js';
import { parseMinorUnits } from './money.js';

export type AdjustmentDirection = 'credit' | 'debit';

/** Mirrors `adminAdjustBalanceRequestSchema.narration`: at least 3 characters, at most 500. */
export const NARRATION_MAX = 500;
export const NARRATION_MIN = 3;

export type AmountCheck =
  | { readonly ok: true; readonly minorUnits: string }
  | { readonly ok: false; readonly error: string };

export type NarrationCheck = { readonly ok: true } | { readonly ok: false; readonly error: string };

/** Minor-unit decimals per currency, matching `lib/money.ts`'s table. */
const DECIMALS: Readonly<Record<string, number>> = {
  NGN: 2,
  USD: 2,
  USDT: 2,
  USDC: 2,
  BTC: 8,
  ETH: 8,
};

export function decimalsFor(currency: string): number {
  return DECIMALS[currency] ?? 2;
}

/**
 * What the operator typed, as integer minor units, or a sentence saying why it is not an
 * amount.
 *
 * Zero is refused explicitly rather than falling out of some other rule. A zero adjustment
 * is not a small mistake to let through: it writes a narration and an audit row describing a
 * movement that did not happen, which is worse than nothing having been recorded at all.
 *
 * Negative is refused too. "Debit" is a direction, chosen with a button, not a minus sign
 * typed into a box — an amount field that accepts `-5000` gives an operator two ways to say
 * the same thing and one way to say the opposite of what they meant.
 */
export function parseAdjustmentAmount(input: string, currency: string): AmountCheck {
  const raw = input.trim();
  if (raw === '') return { ok: false, error: 'Enter an amount.' };
  if (raw.startsWith('-')) {
    return {
      ok: false,
      error: 'Enter a positive amount. Use the Debit button to take money out.',
    };
  }

  const decimals = decimalsFor(currency);
  const minor = parseMinorUnits(raw, decimals);
  if (minor === null) {
    return {
      ok: false,
      error:
        decimals === 0
          ? 'A whole number, with no decimal point.'
          : `A plain amount with at most ${String(decimals)} decimal places, like 25,000.00.`,
    };
  }
  if (minor === 0n) return { ok: false, error: 'Zero moves nothing. Enter an amount above zero.' };
  return { ok: true, minorUnits: minor.toString() };
}

/** The narration, judged on what it will have to explain to somebody a year from now. */
export function checkNarration(narration: string): NarrationCheck {
  const trimmed = narration.trim();
  if (trimmed === '') {
    return {
      ok: false,
      error: 'Say why this money is moving. This is the only record of the reason.',
    };
  }
  if (trimmed.length < NARRATION_MIN) {
    return {
      ok: false,
      error: `At least ${String(NARRATION_MIN)} characters. A one-letter note explains nothing to whoever reads it next.`,
    };
  }
  if (trimmed.length > NARRATION_MAX) {
    return { ok: false, error: `Keep it under ${String(NARRATION_MAX)} characters.` };
  }
  return { ok: true };
}

/**
 * A fresh idempotency key for one adjustment.
 *
 * `adminAdjustBalanceRequestSchema` requires one rather than accepting it optionally, and the
 * contract says why: a double-tapped credit button is the single most likely way this endpoint
 * loses money, and it is the one duplicate an operator cannot see afterwards without
 * reconciling by hand.
 *
 * The key is minted **once per attempt**, when the dialog is armed, and reused across retries
 * of that same attempt. Minting it inside the submit handler would defeat the whole mechanism:
 * two clicks would carry two keys and the server would honour both. `crypto.randomUUID` is the
 * platform CSPRNG; the fallback is only for a browser without it, and is still random rather
 * than a counter, because a predictable key is one another tab can collide with.
 */
export function newIdempotencyKey(): string {
  const uuid =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
          b.toString(16).padStart(2, '0'),
        ).join('');
  // `idempotencyKeySchema` is 16 to 128 URL-safe characters; a uuid's hyphens are URL-safe
  // and are kept, so the value that reaches the audit trail is the one a person can read back.
  return `adj-${uuid}`;
}

/**
 * The balance this adjustment would leave behind, computed in `BigInt` from two integer
 * strings.
 *
 * Shown before the operator confirms, because "what will their balance be" is the question
 * being answered by the whole action and reading it off a receipt afterwards is too late.
 * Null when the two amounts are in different currencies, which is a mismatch the page must
 * say out loud rather than paper over by pretending kobo and cents are the same thing.
 */
export function projectedBalance(
  current: MoneyDTO,
  minorUnits: string,
  direction: AdjustmentDirection,
): MoneyDTO | null {
  if (!/^-?\d+$/.test(current.amount) || !/^\d+$/.test(minorUnits)) return null;
  const delta = BigInt(minorUnits);
  const next = direction === 'credit' ? BigInt(current.amount) + delta : BigInt(current.amount) - delta;
  return { currency: current.currency, amount: next.toString() };
}

/**
 * Whether a debit would take the customer below zero.
 *
 * A warning rather than a block. The API is the authority on whether an overdraw is allowed
 * (a reversal against a spent balance is a legitimate thing a finance desk does), so refusing
 * it here would be the console inventing a policy. What the console owes the operator is
 * knowing before they type the confirmation, not after the API says no.
 */
export function overdraws(current: MoneyDTO, minorUnits: string, direction: AdjustmentDirection): boolean {
  if (direction !== 'debit') return false;
  const next = projectedBalance(current, minorUnits, direction);
  return next !== null && next.amount.startsWith('-');
}
